import { io } from "socket.io-client";
import { ApiConfig } from "../../../api/apiConfig/apiConfig";

/**
 * Shared /options namespace socket — one connection per browser session.
 * Created on first options-page visit; kept alive on navigate-away so re-entry reuses it.
 * Reconnects when JWT auth changes (login/logout/token refresh).
 */
let socket = null;
let refCount = 0;
let authKey = null;
/** Latest `account_update` payload — shared across options pages (trade + wallet). */
let lastAccountUpdate = null;

function normalizeAccountPayload(data) {
    if (!data || typeof data !== "object") return null;
    return data;
}

function attachPersistentListeners(sock) {
    if (sock.__agceOptionsCoreListeners) return;
    sock.__agceOptionsCoreListeners = true;
    sock.on("account_update", (data) => {
        const normalized = normalizeAccountPayload(data);
        if (normalized) lastAccountUpdate = normalized;
    });
}

export function getLastOptionsAccountUpdate() {
    return lastAccountUpdate;
}

export function setLastOptionsAccountUpdate(data) {
    const normalized = normalizeAccountPayload(data);
    lastAccountUpdate = normalized;
    return normalized;
}

const SOCKET_OPTS_BASE = {
    transports: ["websocket"],
    upgrade: false,
    reconnection: true,
    reconnectionAttempts: 5,
};

function getOptionsAccessToken() {
    try {
        const raw = localStorage.getItem("token") || sessionStorage.getItem("token");
        return raw ? String(raw).replace(/^Bearer\s+/i, "") : null;
    } catch {
        return null;
    }
}

function teardownSocket() {
    if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
    }
    socket = null;
    authKey = null;
    lastAccountUpdate = null;
}

function createSocket(accessToken) {
    const opts = { ...SOCKET_OPTS_BASE };
    if (accessToken) {
        opts.auth = { token: accessToken };
    }
    const sock = io(`${ApiConfig.webSocketUrl}/options`, opts);
    sock.io.on("reconnect_attempt", () => {
        const next = getOptionsAccessToken();
        sock.auth = next ? { token: next } : {};
    });
    return sock;
}

/**
 * Sync options socket handshake auth with storage (login / refresh / logout).
 */
export function syncOptionsSocketAuth() {
    const accessToken = getOptionsAccessToken();
    const nextAuthKey = accessToken || "";

    if (socket && authKey === nextAuthKey) {
        if (socket && !socket.connected && refCount > 0) {
            socket.auth = accessToken ? { token: accessToken } : {};
            socket.connect();
        }
        return socket;
    }

    const keepAlive = refCount > 0 || Boolean(socket);
    teardownSocket();
    authKey = nextAuthKey;

    if (!keepAlive) return null;

    socket = createSocket(accessToken || null);
    attachPersistentListeners(socket);
    if (refCount > 0) {
        socket.connect();
    }
    return socket;
}

/**
 * Acquire the shared options socket. Creates on first use; reuses on later visits.
 */
export function acquireOptionsSocket(accessToken) {
    const token = accessToken || getOptionsAccessToken();
    const nextAuthKey = token || "";
    refCount += 1;

    if (socket && authKey !== nextAuthKey) {
        teardownSocket();
    }

    authKey = nextAuthKey;

    if (!socket) {
        socket = createSocket(token || null);
        attachPersistentListeners(socket);
    } else {
        socket.auth = token ? { token } : {};
    }

    if (!socket.connected) {
        socket.connect();
    }

    return socket;
}

/** Release a page consumer. Does NOT disconnect — socket stays warm for the session. */
export function releaseOptionsSocket() {
    refCount = Math.max(0, refCount - 1);
}

export function getOptionsSocket() {
    return socket;
}

/** Tear down on full page unload (optional hygiene). */
if (typeof window !== "undefined") {
    window.addEventListener("beforeunload", teardownSocket);
    window.addEventListener("agce-auth-changed", syncOptionsSocketAuth);
    window.addEventListener("agce-auth-token-changed", syncOptionsSocketAuth);
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState !== "visible") return;
        const token = getOptionsAccessToken();
        if (!socket) return;
        socket.auth = token ? { token } : {};
        if (!socket.connected && refCount > 0) {
            socket.connect();
        }
    });
}
